import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSeo } from '../../utils/seo';

interface ContactInfo {
  id: number;
  title: string;
  description: string;
  email: string;
  icon: string;
  color: string;
}

const contacts: ContactInfo[] = [
  {
    id: 1,
    title: 'Техническая поддержка',
    description: 'Вопросы по работе сайта, проблемы с промокодами, техническая помощь',
    email: 'support@promokod.online',
    icon: 'ri-customer-service-line',
    color: 'blue'
  },
  {
    id: 2,
    title: 'Реклама на сайте',
    description: 'Размещение рекламы, партнёрские программы, медиакит',
    email: 'advertising@promokod.online',
    icon: 'ri-advertisement-line',
    color: 'green'
  },
  {
    id: 3,
    title: 'Предложения и сотрудничество',
    description: 'Новые идеи, предложения по улучшению сервиса, партнёрство',
    email: 'partnership@promokod.online',
    icon: 'ri-handshake-line',
    color: 'purple'
  }
];

export default function Contacts() {
  usePageSeo({
    title: 'Контакты | Promokod.online',
    description: 'Контакты Promokod.online для поддержки, рекламы и сотрудничества. Свяжитесь с нашей командой по вопросам промокодов, размещения и партнёрских предложений.',
    canonicalPath: '/contacts',
  });

  const copyToClipboard = (email: string) => {
    navigator.clipboard.writeText(email);
    // Здесь можно добавить уведомление об успешном копировании
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Свяжитесь с нами
          </h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Мы всегда готовы помочь! Выберите подходящий способ связи для решения вашего вопроса
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contacts.map(contact => (
              <div key={contact.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow p-8 text-center flex flex-col h-full">
                <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
                  contact.color === 'blue' ? 'bg-blue-100' :
                  contact.color === 'green' ? 'bg-blue-100' : 'bg-blue-100'
                }`}>
                  <i className={`${contact.icon} text-2xl ${
                    contact.color === 'blue' ? 'text-blue-600' :
                    contact.color === 'green' ? 'text-blue-600' : 'text-blue-600'
                  }`}></i>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {contact.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed flex-1">
                  {contact.description}
                </p>
                
                <div className="space-y-3 mt-auto">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                    contact.color === 'blue' ? 'bg-blue-50 text-blue-700' :
                    contact.color === 'green' ? 'bg-blue-50 text-blue-700' : 'bg-blue-50 text-blue-700'
                  } font-medium`}>
                    <i className="ri-mail-line"></i>
                    <span>{contact.email}</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <a
                      href={`mailto:${contact.email}`}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        contact.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                        contact.color === 'green' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      Написать письмо
                    </a>
                    
                    <button
                      onClick={() => copyToClipboard(contact.email)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Скопировать email"
                    >
                      <i className="ri-file-copy-line text-gray-600"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Часто задаваемые вопросы
            </h2>
            <p className="text-lg text-gray-600">
              Возможно, ответ на ваш вопрос уже есть здесь
            </p>
          </div>

          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Как использовать промокод?
              </h3>
              <p className="text-gray-600">
                Скопируйте промокод, перейдите на сайт магазина, добавьте товары в корзину и введите промокод в специальное поле при оформлении заказа.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Почему промокод не работает?
              </h3>
              <p className="text-gray-600">
                Промокод может не работать по нескольким причинам: истёк срок действия, не выполнены условия акции, промокод уже использован или не подходит для выбранных товаров.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Как часто обновляются промокоды?
              </h3>
              <p className="text-gray-600">
                Мы обновляем промокоды ежедневно, проверяем их актуальность и добавляем новые предложения от партнёрских магазинов.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Можно ли предложить новый магазин для добавления?
              </h3>
              <p className="text-gray-600">
                Конечно! Напишите нам на partnership@promokod.online с предложением нового магазина, и мы рассмотрим возможность сотрудничества.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-gradient-to-r from-blue-50 to-blue-100 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Напишите нам
            </h2>
            <p className="text-lg text-gray-600">
              Заполните форму, и мы свяжемся с вами в ближайшее время
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-8">
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ваше имя *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Введите ваше имя"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Тема обращения *
                </label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8">
                  <option value="">Выберите тему</option>
                  <option value="support">Техническая поддержка</option>
                  <option value="advertising">Реклама на сайте</option>
                  <option value="partnership">Предложения и сотрудничество</option>
                  <option value="other">Другое</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Сообщение *
                </label>
                <textarea
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Опишите ваш вопрос или предложение..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Максимум 500 символов</p>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                >
                  Отправить сообщение
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
